export interface InventoryCapabilities {
  read: boolean;
  receive: boolean;
  count: boolean;
  supervise: boolean;
  export: boolean;
}

/**
 * UI affordances only, derived from validated session groups. Backend inventory
 * authorization remains authoritative for every request. Do not use these roles
 * to promote a warehouse identity into global staff/admin access.
 */
export function inventoryCapabilities(
  groups: readonly string[] | undefined,
): InventoryCapabilities {
  const membership = new Set(groups);
  if (membership.has("bench-approvers")) {
    return {
      read: true,
      receive: true,
      count: true,
      supervise: true,
      export: true,
    };
  }
  if ([...membership].some((group) => group.startsWith("bench-customer"))) {
    return {
      read: false,
      receive: false,
      count: false,
      supervise: false,
      export: false,
    };
  }
  const auditor = membership.has("bench-auditors");
  const operator = membership.has("bench-warehouse-operators");
  const supervisor = membership.has("bench-inventory-supervisors");
  const read = auditor || operator || supervisor;
  return {
    read,
    receive: !auditor && operator,
    count: !auditor && operator,
    supervise: !auditor && supervisor,
    export: auditor || supervisor,
  };
}

const ACCESS_MESSAGES = {
  en: {
    loading: "Checking access…",
    denied: "Access restricted",
    customerDenied: "Customer accounts cannot access the admin panel.",
    unidentifiedDenied: "Your account has no access to the admin panel.",
    inventoryDenied: "Your account has no inventory access.",
    inventoryOnlyDenied: "Your account can access inventory only.",
    inventory: "Inventory",
    warehouse: "Warehouse operator",
    supervisor: "Inventory supervisor",
    warehouseSupervisor: "Warehouse operator / inventory supervisor",
  },
  nl: {
    loading: "Toegang controleren…",
    denied: "Toegang beperkt",
    customerDenied: "Klantaccounts hebben geen toegang tot het beheerpaneel.",
    unidentifiedDenied: "Uw account heeft geen toegang tot het beheerpaneel.",
    inventoryDenied: "Uw account heeft geen toegang tot de voorraad.",
    inventoryOnlyDenied: "Uw account heeft alleen toegang tot de voorraad.",
    inventory: "Voorraad",
    warehouse: "Magazijnmedewerker",
    supervisor: "Voorraadbeheerder",
    warehouseSupervisor: "Magazijnmedewerker / voorraadbeheerder",
  },
  fr: {
    loading: "Vérification de l’accès…",
    denied: "Accès restreint",
    customerDenied:
      "Les comptes clients ne peuvent pas accéder à l’administration.",
    unidentifiedDenied: "Votre compte n’a pas accès à l’administration.",
    inventoryDenied: "Votre compte n’a pas accès au stock.",
    inventoryOnlyDenied: "Votre compte peut uniquement accéder au stock.",
    inventory: "Stock",
    warehouse: "Opérateur d’entrepôt",
    supervisor: "Superviseur de stock",
    warehouseSupervisor: "Opérateur d’entrepôt / superviseur de stock",
  },
} as const;

/** Small, isolated locale copy for the inventory access boundary. */
export function inventoryAccessMessages(language: string | undefined) {
  const locale = language?.toLowerCase().split(/[-_]/)[0];
  return ACCESS_MESSAGES[locale === "nl" || locale === "fr" ? locale : "en"];
}
